import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SMSFValidationEngine } from './SMSFValidationEngine';

const SMSFValidationPanel = ({ smsfState, onNavigateToSection }) => {
    const [validation, setValidation] = useState({
        errors: [],
        warnings: [],
        info: [],
        isValid: true,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0
    });
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        // Run comprehensive validation
        // Derive a reliable total super balance (TSB): prefer explicit balance, else sum member balances
        const derivedTSB = (() => {
            const bal = smsfState.smsfData?.balance;
            if (typeof bal === 'number' && bal >= 0) return bal;
            const members = smsfState.smsfData?.members || [];
            const sum = members.reduce((acc, m) => acc + (Number(m?.balance) || 0), 0);
            return sum;
        })();

        const result = SMSFValidationEngine.validateAll({
            fundName: smsfState.smsfData?.fundName,
            abn: smsfState.smsfData?.abn,
            tfn: smsfState.smsfData?.tfn,
            numberOfMembers: smsfState.smsfData?.numberOfMembers,
            trusteeType: smsfState.smsfData?.trusteeType,
            trusteeCount: smsfState.smsfTrusteeCount,
            balance: derivedTSB,
            contributions: {
                concessional: smsfState.smsfConcessionalUsed,
                nonConcessional: smsfState.smsfNonConcessionalUsed
            },
            totalSuperBalance: derivedTSB,
            age: smsfState.memberProfile?.age || 45,
            members: smsfState.smsfData?.members || [],
            fees: {
                auditFees: smsfState.smsfAuditFees,
                adminFees: smsfState.smsfAdminFees,
                accountingFees: smsfState.smsfAccountingFees
            }
        });
        setValidation(result);
    }, [smsfState]);

    // Auto-expand when there are errors
    useEffect(() => {
        if (validation.errorCount > 0) {
            setExpanded(true);
        }
    }, [validation.errorCount]);

    if (validation.errorCount === 0 && validation.warningCount === 0 && validation.infoCount === 0) {
        return (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <span className="text-2xl mr-3">✅</span>
                    <div>
                        <h3 className="font-semibold text-green-800">All Validations Passed</h3>
                        <p className="text-sm text-green-700">No issues detected with current SMSF configuration</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
            {/* Header */}
            <div 
                className={`p-4 cursor-pointer ${
                    validation.errorCount > 0 ? 'bg-red-50' :
                    validation.warningCount > 0 ? 'bg-yellow-50' :
                    'bg-blue-50'
                }`}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <span className="text-2xl mr-3">
                            {validation.errorCount > 0 ? '❌' :
                             validation.warningCount > 0 ? '⚠️' : 'ℹ️'}
                        </span>
                        <div>
                            <h3 className={`font-semibold ${
                                validation.errorCount > 0 ? 'text-red-800' :
                                validation.warningCount > 0 ? 'text-yellow-800' :
                                'text-blue-800'
                            }`}>
                                Validation Status
                            </h3>
                            <p className={`text-sm ${
                                validation.errorCount > 0 ? 'text-red-700' :
                                validation.warningCount > 0 ? 'text-yellow-700' :
                                'text-blue-700'
                            }`}>
                                {validation.errorCount > 0 && `${validation.errorCount} error${validation.errorCount !== 1 ? 's' : ''}`}
                                {validation.errorCount > 0 && validation.warningCount > 0 && ', '}
                                {validation.warningCount > 0 && `${validation.warningCount} warning${validation.warningCount !== 1 ? 's' : ''}`}
                                {(validation.errorCount > 0 || validation.warningCount > 0) && validation.infoCount > 0 && ', '}
                                {validation.infoCount > 0 && `${validation.infoCount} info message${validation.infoCount !== 1 ? 's' : ''}`}
                            </p>
                        </div>
                    </div>
                    <button className="text-gray-600 hover:text-gray-800">
                        {expanded ? '▼' : '▶'}
                    </button>
                </div>
            </div>
            <p className="px-4 pb-2 text-xs text-gray-500 border-b border-gray-100">
                This tool checks inputs against current rules. It is not financial advice. Seek a licensed adviser for your situation.
            </p>

            {/* Expandable Content */}
            {expanded && (
                <div className="p-4 space-y-4">
                    {/* Errors */}
                    {validation.errors.length > 0 && (
                        <div>
                            <h4 className="font-semibold text-red-800 mb-2 flex items-center">
                                <span className="mr-2">❌</span>
                                Errors ({validation.errors.length})
                            </h4>
                            <div className="space-y-2">
                                {validation.errors.map((error, index) => (
                                    <div key={index} className="border-l-4 border-red-500 bg-red-50 p-3 rounded-r-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <p className="font-medium text-red-800">{error.message}</p>
                                                {error.remedy && (
                                                    <p className="text-sm text-red-700 mt-1">
                                                        <strong>Solution:</strong> {error.remedy}
                                                    </p>
                                                )}
                                                {error.field && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        Field: {error.field}
                                                        {error.memberIndex !== undefined && ` (Member ${error.memberIndex + 1})`}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-xs text-red-600 ml-2 whitespace-nowrap">
                                                {error.code}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Warnings */}
                    {validation.warnings.length > 0 && (
                        <div>
                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                                <span className="mr-2">⚠️</span>
                                Warnings ({validation.warnings.length})
                            </h4>
                            <div className="space-y-2">
                                {validation.warnings.map((warning, index) => (
                                    <div key={index} className="border-l-4 border-yellow-500 bg-yellow-50 p-3 rounded-r-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <p className="font-medium text-yellow-800">{warning.message}</p>
                                                {warning.remedy && (
                                                    <p className="text-sm text-yellow-700 mt-1">
                                                        <strong>Note (not advice):</strong> {warning.remedy}
                                                    </p>
                                                )}
                                                {warning.field && (
                                                    <p className="text-xs text-yellow-600 mt-1">
                                                        Field: {warning.field}
                                                        {warning.memberIndex !== undefined && ` (Member ${warning.memberIndex + 1})`}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-xs text-yellow-600 ml-2 whitespace-nowrap">
                                                {warning.code}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Info */}
                    {validation.info.length > 0 && (
                        <div>
                            <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                                <span className="mr-2">ℹ️</span>
                                Information ({validation.info.length})
                            </h4>
                            <div className="space-y-2">
                                {validation.info.map((info, index) => (
                                    <div key={index} className="border-l-4 border-blue-500 bg-blue-50 p-3 rounded-r-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <p className="font-medium text-blue-800">{info.message}</p>
                                                {info.remedy && (
                                                    <p className="text-sm text-blue-700 mt-1">
                                                        <strong>Note:</strong> {info.remedy}
                                                    </p>
                                                )}
                                                {info.field && (
                                                    <p className="text-xs text-blue-600 mt-1">
                                                        Field: {info.field}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-xs text-blue-600 ml-2 whitespace-nowrap">
                                                {info.code}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons - navigational only; no advice */}
                    <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Last validated: {new Date().toLocaleTimeString()}
                        </div>
                        <div className="flex space-x-2">
                            {validation.errorCount > 0 && typeof onNavigateToSection === 'function' && (
                                <button
                                    type="button"
                                    onClick={() => onNavigateToSection('contributions')}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                >
                                    Go to contributions
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setExpanded(false)}
                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

SMSFValidationPanel.propTypes = {
    smsfState: PropTypes.object.isRequired,
    onNavigateToSection: PropTypes.func
};

export default SMSFValidationPanel;
